import { useState, FormEvent } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const FORM_URL = "https://readdy.ai/api/form/d6u50vi9puk3bj9673t0";

const contactDetails = [
  { icon: "ri-mail-line", label: "Email", value: "hello@calmspacesjournal.com" },
  { icon: "ri-instagram-line", label: "Instagram", value: "@calmspacesjournal" },
  { icon: "ri-pinterest-line", label: "Pinterest", value: "Calm Spaces Journal" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [charCount, setCharCount] = useState(0);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "message") {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setStatus("loading");
    try {
      const body = new URLSearchParams(form as Record<string, string>);
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setStatus(res.ok ? "success" : "error");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="bg-[#FAF7F0] min-h-screen">
      <Navbar />

      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="pt-44 pb-16 px-8 md:px-16 max-w-screen-xl mx-auto">
        <div className="max-w-lg">
          <span
            className="inline-flex items-center gap-2 text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-8"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <span className="w-6 h-px bg-[#C8BAA8] inline-block" />
            Get in Touch
          </span>

          <h1
            className="text-[#2C2825] text-6xl md:text-7xl leading-[1.05] mb-6"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
          >
            <em>Contact</em>
          </h1>

          <p
            className="text-[#8B8378] text-base leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
          >
            Questions, collaborations, or a simple hello — feel free to get in touch.
          </p>
        </div>
      </section>

      {/* ── Main content ─────────────────────────────────── */}
      <section className="pb-32 px-8 md:px-16 max-w-screen-xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20 items-start">

          {/* Left — Image + contact details */}
          <div className="lg:col-span-5 flex flex-col gap-10">
            {/* Tall editorial image */}
            <div className="overflow-hidden rounded-2xl aspect-[4/5] bg-[#F5F1E8] w-full">
              <img
                src="https://public.readdy.ai/ai/img_res/8cee7994740f22aebfff24509a82ee2e.jpg"
                alt="Calm Spaces Journal contact"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Contact info */}
            <div className="space-y-6">
              <p
                className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Find Us Here
              </p>
              {contactDetails.map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#EDE6DA] rounded-full flex-shrink-0">
                    <i className={`${item.icon} text-[#8B8378] text-base`} />
                  </div>
                  <div>
                    <p
                      className="text-[#B5A898] text-[10px] tracking-[0.2em] uppercase mb-0.5"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {item.label}
                    </p>
                    <p
                      className="text-[#3A3632] text-sm"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Form */}
          <div className="lg:col-span-7 lg:pt-4">
            {status === "success" ? (
              /* ── Success state ── */
              <div className="flex flex-col items-center justify-center py-28 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-[#EDE6DA] rounded-full mb-7">
                  <i className="ri-check-line text-2xl text-[#6B5E53]" />
                </div>
                <h2
                  className="text-[#2C2825] text-3xl mb-4"
                  style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
                >
                  Message Sent
                </h2>
                <p
                  className="text-[#8B8378] text-sm leading-relaxed max-w-xs"
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                >
                  Thank you for reaching out. We&apos;ll be in touch soon.
                </p>
              </div>
            ) : (
              /* ── Form ── */
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-8"
              >
                {/* Name */}
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="contact-name"
                    className="text-[#B5A898] text-[10px] tracking-[0.35em] uppercase"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Name
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    className="w-full px-5 py-4 rounded-xl border border-[#D9CEBC] bg-[#F5F1E8] text-[#2C2825] text-sm placeholder-[#C8BAA8] focus:outline-none focus:border-[#8B8378] focus:bg-[#FAF7F0] transition-all duration-200"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>

                {/* Email */}
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="contact-email"
                    className="text-[#B5A898] text-[10px] tracking-[0.35em] uppercase"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Email
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    className="w-full px-5 py-4 rounded-xl border border-[#D9CEBC] bg-[#F5F1E8] text-[#2C2825] text-sm placeholder-[#C8BAA8] focus:outline-none focus:border-[#8B8378] focus:bg-[#FAF7F0] transition-all duration-200"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>

                {/* Message */}
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="contact-message"
                    className="text-[#B5A898] text-[10px] tracking-[0.35em] uppercase"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Write your message here..."
                    rows={7}
                    required
                    className="w-full px-5 py-4 rounded-xl border border-[#D9CEBC] bg-[#F5F1E8] text-[#2C2825] text-sm placeholder-[#C8BAA8] focus:outline-none focus:border-[#8B8378] focus:bg-[#FAF7F0] transition-all duration-200 resize-none"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                  <div className="flex justify-end">
                    <span
                      className={`text-[10px] ${charCount > 450 ? "text-amber-500" : "text-[#C8BAA8]"}`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {charCount} / 500
                    </span>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex flex-col gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={status === "loading" || charCount > 500}
                    className="w-full bg-[#2C2825] text-[#FAF7F0] py-4 rounded-full text-xs tracking-[0.2em] hover:bg-[#3A3632] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 uppercase"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {status === "loading" ? "Sending..." : "Send Message"}
                  </button>
                  <p
                    className="text-[#C8BAA8] text-xs text-center"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    We&apos;ll reply as soon as possible.
                  </p>
                </div>

                {status === "error" && (
                  <p
                    className="text-red-400 text-xs text-center -mt-2"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Something went wrong. Please try again.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
